import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { downloadCsv, parseCsv } from "../lib/csv";
import { useOrganization } from "../organization/OrganizationProvider";

type CustomerRow = {
  id: string;
  customer_type: "person" | "company";
  document_type: string | null;
  document_number: string | null;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  city: string | null;
  preferred_contact: "whatsapp" | "phone" | "email" | null;
  status: "active" | "inactive" | "archived";
  updated_at: string;
};

type CustomerForm = {
  customerType: "person" | "company";
  name: string;
  documentType: string;
  documentNumber: string;
  contactName: string;
  email: string;
  phone: string;
  whatsapp: string;
  address: string;
  city: string;
  preferredContact: "" | "whatsapp" | "phone" | "email";
};

const EMPTY_FORM: CustomerForm = {
  customerType: "person",
  name: "",
  documentType: "DNI",
  documentNumber: "",
  contactName: "",
  email: "",
  phone: "",
  whatsapp: "",
  address: "",
  city: "",
  preferredContact: "whatsapp",
};

function nullable(value: string) {
  const clean = value.trim();
  return clean ? clean : null;
}

export function ClientsPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CustomerForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sunatBusy, setSunatBusy] = useState(false);
  const [sunatStatus, setSunatStatus] = useState("");
  const [sunatCondition, setSunatCondition] = useState("");
  const [sunatSource, setSunatSource] = useState("");
  const lastSunatLookupRef = useRef("");

  const loadCustomers = useCallback(
    async (query: string) => {
      if (!organizationId) {
        setCustomers([]);
        setLoadError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const { data, error } = await supabase.rpc("list_organization_customers", {
        p_organization_id: organizationId,
        p_search: nullable(query),
      });

      if (error) {
        setCustomers([]);
        setLoadError(error.message);
        setLoading(false);
        return;
      }

      setCustomers((data ?? []) as CustomerRow[]);
      setLoading(false);
    },
    [organizationId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadCustomers(search);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadCustomers, search]);

  useEffect(() => {
    setSearch("");
    setForm(EMPTY_FORM);
    setFormOpen(false);
    setFormError(null);
    setFormSuccess(null);
  }, [organizationId]);

  const activeCount = useMemo(
    () => customers.filter((customer) => customer.status === "active").length,
    [customers],
  );

  function downloadCustomerTemplate() {
    downloadCsv(
      "plantilla-clientes.csv",
      [
        "customer_type",
        "document_type",
        "document_number",
        "name",
        "contact_name",
        "email",
        "phone",
        "whatsapp",
        "address",
        "city",
        "preferred_contact",
      ],
      [
        {
          customer_type: "company",
          document_type: "RUC",
          document_number: "20123456789",
          name: "EMPRESA EJEMPLO SAC",
          contact_name: "Contacto",
          email: "contacto@empresa.pe",
          phone: "999999999",
          whatsapp: "999999999",
          address: "Av. Ejemplo 123",
          city: "Lima",
          preferred_contact: "whatsapp",
        },
      ],
    );
  }

  async function handleCustomerCsv(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !organizationId) return;

    setBulkBusy(true);
    setFormError(null);
    setFormSuccess(null);

    const rows = parseCsv(await file.text());
    let created = 0;
    const errors: string[] = [];

    for (const [index, row] of rows.entries()) {
      if (!row.name?.trim()) {
        errors.push(`Fila ${index + 2}: falta name.`);
        continue;
      }

      const { data: customerId, error } = await supabase.rpc(
        "create_organization_customer",
        {
          p_organization_id: organizationId,
          p_customer_type: row.customer_type === "company" ? "company" : "person",
          p_name: row.name.trim(),
          p_document_type: nullable(row.document_type || ""),
          p_document_number: nullable(row.document_number || ""),
          p_contact_name: nullable(row.contact_name || ""),
          p_email: nullable(row.email || ""),
          p_phone: nullable(row.phone || ""),
          p_whatsapp: nullable(row.whatsapp || ""),
          p_city: nullable(row.city || ""),
          p_preferred_contact: nullable(row.preferred_contact || ""),
        },
      );

      if (error) {
        errors.push(`Fila ${index + 2}: ${error.message}`);
      } else {
        if (customerId && row.address?.trim()) {
          await supabase.rpc("update_organization_customer", {
            p_customer_id: customerId,
            p_customer_type: row.customer_type === "company" ? "company" : "person",
            p_name: row.name.trim(),
            p_document_type: nullable(row.document_type || ""),
            p_document_number: nullable(row.document_number || ""),
            p_contact_name: nullable(row.contact_name || ""),
            p_email: nullable(row.email || ""),
            p_phone: nullable(row.phone || ""),
            p_whatsapp: nullable(row.whatsapp || ""),
            p_address: nullable(row.address || ""),
            p_city: nullable(row.city || ""),
            p_country_code: "PE",
            p_notes: null,
            p_preferred_contact: nullable(row.preferred_contact || ""),
          });
        }
        created += 1;
      }
    }

    setBulkBusy(false);
    setFormSuccess(
      errors.length
        ? `Carga terminada: ${created} clientes creados, ${errors.length} incidencias.`
        : `Carga masiva completada: ${created} clientes creados.`,
    );
    if (errors.length) setFormError(errors.slice(0, 5).join(" "));
    await loadCustomers("");
  }

  async function lookupSunatRuc() {
    if (!organizationId) return;

    const ruc = form.documentNumber.trim();

    if (!/^\d{11}$/.test(ruc)) {
      setFormError("Ingresa un RUC válido de 11 dígitos.");
      return;
    }

    setSunatBusy(true);
    setFormError(null);
    setFormSuccess(null);

    const { data, error } = await supabase.functions.invoke("sunat-ruc-lookup", {
      body: {
        ruc,
        organization_id: organizationId,
      },
    });

    setSunatBusy(false);

    if (error || !data?.ok || !data?.data) {
      const code = data?.code;
      const diagnostics = Array.isArray(data?.diagnostics)
        ? data.diagnostics.join(" · ")
        : "";

      setFormError(
        code === "RUC_INVALID"
          ? "El RUC no es válido."
          : code === "RUC_NOT_FOUND"
            ? "SUNAT no encontró ese RUC."
            : diagnostics
              ? `No se pudo completar el RUC. Detalle técnico: ${diagnostics}`
              : "SUNAT no respondió a la consulta automática. Puedes usar la consulta manual.",
      );
      return;
    }

    const fiscal = data.data as {
      ruc?: string;
      name?: string;
      address?: string;
      phone?: string;
      city?: string;
      state_code?: string;
      domicile_condition_code?: string;
      department?: string;
      province?: string;
      district?: string;
    };

    const resolvedCity =
      fiscal.city ||
      fiscal.district ||
      fiscal.province ||
      fiscal.department ||
      "";

    setForm((current) => ({
      ...current,
      customerType:
        ruc.startsWith("20") || ruc.startsWith("15")
          ? "company"
          : ruc.startsWith("10")
            ? "person"
            : current.customerType,
      documentType: "RUC",
      documentNumber: fiscal.ruc || ruc,
      name: fiscal.name || current.name,
      address: fiscal.address || current.address,
      city: resolvedCity || current.city,
      phone: fiscal.phone || current.phone,
    }));

    setSunatStatus(fiscal.state_code || "");
    setSunatCondition(fiscal.domicile_condition_code || "");
    setSunatSource(String(data.source || "SUNAT"));
    lastSunatLookupRef.current = ruc;

    setFormSuccess(
      `Datos fiscales completados desde ${String(data.source || "SUNAT")}.`,
    );
  }

  useEffect(() => {
    const ruc = form.documentNumber.trim();

    if (
      !formOpen ||
      form.documentType.toUpperCase() !== "RUC" ||
      !/^\d{11}$/.test(ruc) ||
      sunatBusy ||
      lastSunatLookupRef.current === ruc
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      void lookupSunatRuc();
    }, 450);

    return () => window.clearTimeout(timer);
  }, [
    formOpen,
    form.documentType,
    form.documentNumber,
    sunatBusy,
  ]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId) return;

    setSaving(true);
    setFormError(null);
    setFormSuccess(null);

    const { data: customerId, error } = await supabase.rpc(
      "create_organization_customer",
      {
        p_organization_id: organizationId,
        p_customer_type: form.customerType,
        p_name: form.name,
        p_document_type: nullable(form.documentType),
        p_document_number: nullable(form.documentNumber),
        p_contact_name: nullable(form.contactName),
        p_email: nullable(form.email),
        p_phone: nullable(form.phone),
        p_whatsapp: nullable(form.whatsapp),
        p_city: nullable(form.city),
        p_preferred_contact: nullable(form.preferredContact),
      },
    );

    if (!error && customerId && form.address.trim()) {
      const { error: addressError } = await supabase.rpc(
        "update_organization_customer",
        {
          p_customer_id: customerId,
          p_customer_type: form.customerType,
          p_name: form.name,
          p_document_type: nullable(form.documentType),
          p_document_number: nullable(form.documentNumber),
          p_contact_name: nullable(form.contactName),
          p_email: nullable(form.email),
          p_phone: nullable(form.phone),
          p_whatsapp: nullable(form.whatsapp),
          p_address: nullable(form.address),
          p_city: nullable(form.city),
          p_country_code: "PE",
          p_notes: null,
          p_preferred_contact: nullable(form.preferredContact),
        },
      );

      if (addressError) {
        setSaving(false);
        setFormError(addressError.message);
        return;
      }
    }

    setSaving(false);

    if (error) {
      const duplicate =
        error.code === "23505" ||
        error.message.toLowerCase().includes("customers_org_document_uq");

      setFormError(
        duplicate
          ? "Ya existe un cliente con ese tipo y número de documento en esta empresa."
          : error.message,
      );
      return;
    }

    setForm(EMPTY_FORM);
    setFormSuccess("Cliente creado correctamente.");
    await loadCustomers("");
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con clientes.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Clientes</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Personas y empresas vinculadas a la operación.</p>
        </div>

        <div className="page-header-buttons">
          <button className="ghost-button" type="button" onClick={downloadCustomerTemplate}>
            Descargar plantilla
          </button>
          <label className="ghost-button file-action-button">
            {bulkBusy ? "Importando…" : "Carga masiva CSV"}
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={bulkBusy}
              onChange={(event) => void handleCustomerCsv(event)}
            />
          </label>
          <button
            className="primary-button"
            type="button"
            onClick={() => {
              setFormOpen((value) => !value);
              setFormError(null);
              setFormSuccess(null);
              setSunatStatus("");
              setSunatCondition("");
              setSunatSource("");
              lastSunatLookupRef.current = "";
            }}
          >
            {formOpen ? "Cerrar" : "Nuevo cliente"}
          </button>
        </div>
      </header>

      {formOpen ? (
        <section className="panel-card customer-form-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Registro</span>
              <h2>Nuevo cliente</h2>
            </div>
          </div>

          <form className="customer-form-grid" onSubmit={handleCreate}>
            <label>
              Tipo
              <select
                value={form.customerType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    customerType: event.target.value as "person" | "company",
                    documentType:
                      event.target.value === "company" ? "RUC" : "DNI",
                  }))
                }
              >
                <option value="person">Persona</option>
                <option value="company">Empresa</option>
              </select>
            </label>

            <label className="field-span-2">
              Nombre / razón social
              <input
                required
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>

            <label>
              Tipo de documento
              <input
                value={form.documentType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    documentType: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Número de documento
              <input
                value={form.documentNumber}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    documentNumber: event.target.value,
                  }))
                }
              />
            </label>

            <div className="customer-sunat-link">
              <span>Datos fiscales</span>
              {form.documentType.toUpperCase() === "RUC" &&
              form.documentNumber.trim().length === 11 ? (
                <div className="customer-sunat-actions">
                  <button
                    className="compact-button"
                    type="button"
                    disabled={sunatBusy}
                    onClick={() => void lookupSunatRuc()}
                  >
                    {sunatBusy ? "Consultando…" : "Buscar y completar desde SUNAT"}
                  </button>
                  <a
                    className="compact-button"
                    href="https://e-consultaruc.sunat.gob.pe/cl-ti-itmrconsruc/FrameCriterioBusquedaWeb.jsp"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir Consulta RUC
                  </a>
                </div>
              ) : (
                <small>Ingresa un RUC de 11 dígitos para consultar SUNAT.</small>
              )}
            </div>

            {sunatStatus || sunatCondition ? (
              <div className="customer-sunat-result field-span-full">
                <strong>Resultado SUNAT</strong>
                <span>
                  {sunatStatus ? `Estado: ${sunatStatus}` : ""}
                  {sunatStatus && sunatCondition ? " · " : ""}
                  {sunatCondition ? `Condición: ${sunatCondition}` : ""}
                  {sunatSource ? ` · Fuente: ${sunatSource}` : ""}
                </span>
              </div>
            ) : null}

            <label>
              Contacto
              <input
                value={form.contactName}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    contactName: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Correo
              <input
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({ ...current, email: event.target.value }))
                }
              />
            </label>

            <label>
              Teléfono
              <input
                value={form.phone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, phone: event.target.value }))
                }
              />
            </label>

            <label>
              WhatsApp
              <input
                value={form.whatsapp}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    whatsapp: event.target.value,
                  }))
                }
              />
            </label>

            <label className="field-span-2">
              Dirección fiscal
              <input
                value={form.address}
                onChange={(event) =>
                  setForm((current) => ({ ...current, address: event.target.value }))
                }
              />
            </label>

            <label>
              Ciudad
              <input
                value={form.city}
                onChange={(event) =>
                  setForm((current) => ({ ...current, city: event.target.value }))
                }
              />
            </label>

            <label>
              Contacto preferido
              <select
                value={form.preferredContact}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    preferredContact: event.target.value as CustomerForm["preferredContact"],
                  }))
                }
              >
                <option value="">Sin preferencia</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="phone">Teléfono</option>
                <option value="email">Correo</option>
              </select>
            </label>

            {formError ? (
              <div className="form-error field-span-full">{formError}</div>
            ) : null}

            {formSuccess ? (
              <div className="form-success field-span-full">{formSuccess}</div>
            ) : null}

            <div className="form-actions field-span-full">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? "Guardando…" : "Guardar cliente"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : customers.length}</strong>
          <span> resultados · {activeCount} activos</span>
        </div>

        <input
          type="search"
          placeholder="Buscar por nombre, documento, correo o teléfono"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          No se pudieron cargar los clientes: {loadError}
        </div>
      ) : null}

      {!loading && customers.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay clientes para mostrar.</strong>
          <p>Crea el primer cliente o cambia el criterio de búsqueda.</p>
        </section>
      ) : null}

      <section className="customer-grid" aria-label="Listado de clientes">
        {customers.map((customer) => (
          <article className="customer-card" key={customer.id}>
            <div className="customer-card-heading">
              <div>
                <span className="status-badge">
                  {customer.customer_type === "company" ? "Empresa" : "Persona"}
                </span>
                <h2>{customer.name}</h2>
              </div>
              <span
                className={
                  customer.status === "active"
                    ? "status-dot status-dot-active"
                    : "status-dot"
                }
                title={customer.status}
              />
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Documento</dt>
                <dd>
                  {customer.document_type && customer.document_number
                    ? `${customer.document_type} ${customer.document_number}`
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>Contacto</dt>
                <dd>{customer.contact_name ?? "—"}</dd>
              </div>
              <div>
                <dt>WhatsApp</dt>
                <dd>{customer.whatsapp ?? "—"}</dd>
              </div>
              <div>
                <dt>Correo</dt>
                <dd>{customer.email ?? "—"}</dd>
              </div>
              <div>
                <dt>Ciudad</dt>
                <dd>{customer.city ?? "—"}</dd>
              </div>
              <div>
                <dt>Preferido</dt>
                <dd>{customer.preferred_contact ?? "—"}</dd>
              </div>
            </dl>

            <div className="quotation-actions">
              <Link className="compact-button" to={`/clientes/${customer.id}`}>
                Abrir ficha
              </Link>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
